import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from '../context/TranslationContext';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Check, Download, FileText, ClipboardList, PenLine, CircleCheck } from 'lucide-react';
import axios from 'axios';
import { API_URL } from '../config/api';
import { ViewQuotePdfDocument } from '../components/ViewQuotePdfDocument';
import { ViewQuoteTermsPdf } from '../components/ViewQuoteTermsPdf';
import { QuotePreviewScaler } from '../components/QuotePreviewScaler';
import { exportPdfPages, exportPdfPagesAsDataUri, renumberQuotePageFooters } from '../lib/pdfPageExport';
import { preloadKeralitProfileImages, prepareKeralitDrawingsForPdfExport } from '../lib/keralitSvg';
import { isKeralitQuote } from '../lib/vmkQuoteTable';
import { openQuotePreviewInNewTab } from '../lib/openQuotePreviewInNewTab';
import '../styles/quotePdf.css';

const TERMS_PDF_PATH = '/documents/VMK_Algemene_Leveringsvoorwaarden_Compleet.pdf';

const FLOW_STEPS = ['quote', 'terms', 'sign', 'success'];

const formatDisplayDate = (locale) => {
  const d = new Date();
  return d.toLocaleDateString(locale === 'nl' ? 'nl-NL' : 'en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const StepIcon = ({ stepKey }) => {
  if (stepKey === 'quote') return <ClipboardList className="h-4 w-4" />;
  if (stepKey === 'terms') return <FileText className="h-4 w-4" />;
  if (stepKey === 'sign') return <PenLine className="h-4 w-4" />;
  return <CircleCheck className="h-4 w-4" />;
};

const ViewQuoteStepper = ({ currentStep, t }) => {
  const currentIndex = FLOW_STEPS.indexOf(currentStep);

  return (
    <nav aria-label={t('viewQuote.flowProgress')} className="w-full">
      <ol className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        {FLOW_STEPS.map((stepKey, index) => {
          const isComplete = currentIndex > index;
          const isCurrent = currentStep === stepKey;
          const labelKey = {
            quote: 'viewQuote.stepReviewOffer',
            terms: 'viewQuote.stepTerms',
            sign: 'viewQuote.stepSign',
            success: 'viewQuote.stepComplete',
          }[stepKey];

          return (
            <li key={stepKey} className="flex flex-1 items-center gap-2 sm:flex-col sm:gap-1.5">
              <div className="flex items-center gap-2 sm:flex-col">
                <span
                  className={[
                    'flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 text-sm font-semibold transition-colors',
                    isComplete
                      ? 'border-green-600 bg-green-600 text-white'
                      : isCurrent
                        ? 'border-orange-600 bg-orange-50 text-orange-700'
                        : 'border-gray-200 bg-white text-gray-400',
                  ].join(' ')}
                >
                  {isComplete ? <Check className="h-4 w-4" /> : <StepIcon stepKey={stepKey} />}
                </span>
                <span
                  className={[
                    'text-sm font-medium sm:text-center',
                    isCurrent ? 'text-gray-900' : isComplete ? 'text-green-700' : 'text-gray-400',
                  ].join(' ')}
                >
                  {t(labelKey)}
                </span>
              </div>
              {index < FLOW_STEPS.length - 1 ? (
                <div
                  className={[
                    'hidden h-0.5 flex-1 sm:block',
                    currentIndex > index ? 'bg-green-500' : 'bg-gray-200',
                  ].join(' ')}
                  aria-hidden
                />
              ) : null}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

const ViewQuotePage = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const { t, language, changeLanguage } = useTranslation();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [step, setStep] = useState('quote');
  const [quoteScrolledToEnd, setQuoteScrolledToEnd] = useState(false);
  const [termsScrolledToEnd, setTermsScrolledToEnd] = useState(false);
  const [termsPageCount, setTermsPageCount] = useState(0);
  const [termsAcceptedAt, setTermsAcceptedAt] = useState(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [signature, setSignature] = useState('');
  const [isDrawing, setIsDrawing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [openingQuotePdf, setOpeningQuotePdf] = useState(false);
  const [signedQuoteNumber, setSignedQuoteNumber] = useState('');
  const [previewZoom, setPreviewZoom] = useState(1);

  const quoteScrollRef = useRef(null);
  const quoteEndRef = useRef(null);
  const termsScrollRef = useRef(null);
  const termsEndRef = useRef(null);
  const signatureCanvasRef = useRef(null);
  const pdfExportRef = useRef(null);

  const locale = language === 'nl' ? 'nl-NL' : 'en-US';
  const formatCurrency = (value) =>
    new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' }).format(value);

  const signedDate = formatDisplayDate(language);

  useEffect(() => {
    const urlLang = searchParams.get('lang');
    if (urlLang === 'nl' || urlLang === 'en') {
      changeLanguage(urlLang);
    }
  }, [searchParams, changeLanguage]);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setError('Missing token');
      return;
    }
    axios
      .get(`${API_URL}/api/view-quote/${token}`)
      .then((res) => {
        setQuote(res.data);
        setName(res.data.signed_by_name || res.data.recipient_name || '');
        setEmail(res.data.recipient_email || '');
        if (!searchParams.get('lang') && (res.data.language === 'nl' || res.data.language === 'en')) {
          changeLanguage(res.data.language);
        }
        if (res.data.signed) {
          setStep('success');
          setSignedQuoteNumber(res.data.quote_number || '');
        }
        setError(null);
      })
      .catch((err) => {
        setError(err.response?.status === 404 ? 'not_found' : err.message);
        setQuote(null);
      })
      .finally(() => setLoading(false));
  }, [token, searchParams, changeLanguage]);

  useEffect(() => {
    if (searchParams.get('signed') === '1' && quote?.signed) {
      setStep('success');
    }
  }, [searchParams, quote?.signed]);

  useEffect(() => {
    const drawings = quote?.drawings;
    if (!Array.isArray(drawings) || !drawings.some((drawing) => isKeralitQuote(drawing, drawing.type))) {
      return;
    }

    let cancelled = false;
    preloadKeralitProfileImages().then(() => {
      if (cancelled || !pdfExportRef.current) return;
      prepareKeralitDrawingsForPdfExport(pdfExportRef.current);
    });

    return () => {
      cancelled = true;
    };
  }, [quote]);

  const bindScrollToEnd = useCallback((container, sentinel, setScrolled) => {
    if (!container || !sentinel) return undefined;

    const checkScroll = () => {
      const containerBottom = container.scrollTop + container.clientHeight;
      const sentinelTop = sentinel.offsetTop - container.offsetTop;
      if (containerBottom >= sentinelTop - 12) {
        setScrolled(true);
      }
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setScrolled(true);
      },
      { root: container, threshold: 0.25 }
    );

    observer.observe(sentinel);
    container.addEventListener('scroll', checkScroll, { passive: true });
    checkScroll();

    return () => {
      observer.disconnect();
      container.removeEventListener('scroll', checkScroll);
    };
  }, []);

  useEffect(() => {
    if (step !== 'quote') return undefined;
    return bindScrollToEnd(quoteScrollRef.current, quoteEndRef.current, setQuoteScrolledToEnd);
  }, [step, quote, bindScrollToEnd]);

  useEffect(() => {
    if (step !== 'terms') return undefined;
    return bindScrollToEnd(termsScrollRef.current, termsEndRef.current, setTermsScrolledToEnd);
  }, [step, termsPageCount, bindScrollToEnd]);

  const getCoords = (e) => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: (clientX - rect.left) * scaleX, y: (clientY - rect.top) * scaleY };
  };

  const startDraw = (e) => {
    e.preventDefault();
    const coords = getCoords(e);
    if (!coords) return;
    setIsDrawing(true);
    const ctx = signatureCanvasRef.current.getContext('2d');
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
  };

  const draw = (e) => {
    e.preventDefault();
    if (!isDrawing) return;
    const coords = getCoords(e);
    if (!coords) return;
    const ctx = signatureCanvasRef.current.getContext('2d');
    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();
  };

  const endDraw = (e) => {
    e.preventDefault();
    if (isDrawing && signatureCanvasRef.current) {
      setSignature(signatureCanvasRef.current.toDataURL('image/png'));
    }
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = signatureCanvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      setSignature('');
    }
  };

  useEffect(() => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#000000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, [step]);

  const handleContinueFromQuote = () => {
    setTermsScrolledToEnd(false);
    setTermsPageCount(0);
    setStep('terms');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleOpenQuotePdf = async () => {
    const container = pdfExportRef.current;
    if (!container) {
      alert(t('viewQuote.openQuotePdfError'));
      return;
    }

    const quoteTitle = `Offerte ${quote?.quote_number || ''}`.trim();
    const viewerWindow = window.open('', '_blank');
    if (!viewerWindow) {
      alert(t('viewQuote.openQuotePopupBlocked'));
      return;
    }

    viewerWindow.document.title = quoteTitle || t('viewQuote.openQuotePdf');
    viewerWindow.document.body.innerHTML =
      '<p style="font-family:sans-serif;padding:24px;color:#334155;">Loading…</p>';

    setOpeningQuotePdf(true);
    try {
      if (document.fonts?.ready) {
        await document.fonts.ready;
      }
      await prepareKeralitDrawingsForPdfExport(container);

      const opened = openQuotePreviewInNewTab(container, {
        title: quoteTitle || t('viewQuote.openQuotePdf'),
        windowRef: viewerWindow,
      });
      if (!opened) {
        viewerWindow.close();
        alert(t('viewQuote.openQuotePdfError'));
      }
    } catch (err) {
      viewerWindow.close();
      alert(t('viewQuote.openQuotePdfError'));
      console.error(err);
    } finally {
      setOpeningQuotePdf(false);
    }
  };

  const handleAcceptTerms = () => {
    setTermsAcceptedAt(new Date().toISOString());
    setStep('sign');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const runSignedQuotePdfExport = async ({ asDataUri = false, filename } = {}) => {
    const container = pdfExportRef.current;
    if (!container) return null;

    container.classList.add('pdf-exporting');
    const restoreFooters = renumberQuotePageFooters(container, '[data-pdf-page]');

    if (document.fonts?.ready) {
      await document.fonts.ready;
    }
    const images = container.querySelectorAll('img');
    await Promise.all(
      [...images].map(
        (img) =>
          img.complete
            ? Promise.resolve()
            : new Promise((resolve) => {
                img.onload = resolve;
                img.onerror = resolve;
              })
      )
    );

    await prepareKeralitDrawingsForPdfExport(container);

    await new Promise((resolve) => setTimeout(resolve, 300));
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

    const exportOptions = {
      scale: 2,
      marginMm: 0,
      pageSelector: '[data-pdf-page]',
    };

    try {
      if (asDataUri) {
        return await exportPdfPagesAsDataUri(container, exportOptions);
      }
      await exportPdfPages(container, filename, exportOptions);
      return true;
    } finally {
      container.classList.remove('pdf-exporting');
      restoreFooters();
    }
  };

  const generateSignedPdfDataUri = async () => {
    try {
      return await runSignedQuotePdfExport({ asDataUri: true });
    } catch (err) {
      console.error('Failed to generate signed quote PDF:', err);
      return null;
    }
  };

  const handleDownloadSignedPdf = async () => {
    setDownloading(true);
    try {
      await runSignedQuotePdfExport({
        filename: `Offerte-${quote?.quote_number || 'signed'}.pdf`,
      });
    } catch (err) {
      alert(t('viewQuote.downloadError'));
      console.error(err);
    } finally {
      setDownloading(false);
    }
  };

  const handleSignSubmit = async () => {
    setSubmitting(true);
    try {
      const quotePdfBase64 = await generateSignedPdfDataUri();
      const res = await axios.post(`${API_URL}/api/view-quote/${token}/sign`, {
        recipient_name: name.trim(),
        recipient_email: email.trim(),
        signature,
        terms_accepted_at: termsAcceptedAt,
        quote_pdf_base64: quotePdfBase64,
      });
      setSignedQuoteNumber(quote?.quote_number || '');
      setStep('success');
      if (!res.data?.email_sent) {
        console.warn('Signed quote saved but confirmation email may have failed');
      }
    } catch (err) {
      alert(t('viewQuote.acceptError') + ': ' + (err.response?.data?.detail || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  const signedAgreement = {
    name: quote?.signed_by_name || name.trim() || quote?.recipient_name || '',
    date: quote?.signed_at
      ? new Date(quote.signed_at).toLocaleDateString(locale, {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        })
      : signedDate,
    signature: quote?.signature || signature || '',
  };

  const clientDetails = {
    name: name.trim() || quote?.recipient_name || '',
    ...(quote?.client_details || {}),
  };

  const total = quote?.total ?? 0;
  const termsPdfUrl = quote?.terms_pdf_url || TERMS_PDF_PATH;

  const quotePreviewDocument = (
    <ViewQuotePdfDocument
      quoteNumber={quote?.quote_number || signedQuoteNumber}
      clientDetails={clientDetails}
      drawings={quote?.drawings || []}
      sections={quote?.sections || []}
      exportOptions={quote?.export_options || {}}
      subtotal={quote?.subtotal}
      signedAgreement={step === 'success' || step === 'sign' ? signedAgreement : null}
      t={t}
    />
  );

  const pdfExportDocument = (
    <ViewQuotePdfDocument
      quoteNumber={quote?.quote_number || signedQuoteNumber}
      clientDetails={clientDetails}
      drawings={quote?.drawings || []}
      sections={quote?.sections || []}
      exportOptions={quote?.export_options || {}}
      subtotal={quote?.subtotal}
      signedAgreement={step === 'success' || step === 'sign' ? signedAgreement : null}
      t={t}
    />
  );

  const OpenPdfButton = ({ href, label, onClick, loadingLabel, loading = false }) => {
    if (href) {
      return (
        <Button type="button" variant="outline" className="w-full" asChild>
          <a href={href} target="_blank" rel="noopener noreferrer">
            {label}
          </a>
        </Button>
      );
    }

    return (
      <Button
        type="button"
        variant="outline"
        className="w-full"
        disabled={loading}
        onClick={onClick}
      >
        {loading ? loadingLabel : label}
      </Button>
    );
  };

  const ZoomControls = ({ zoom, setZoom, min = 0.75, max = 1.5 }) => (
    <div className="flex items-center gap-2 shrink-0">
      <span className="text-sm text-gray-500 mr-1">{t('viewQuote.pdfZoom')}</span>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-9 w-9 text-lg"
        disabled={zoom <= min}
        onClick={() => setZoom((z) => Math.max(min, +(z - 0.1).toFixed(2)))}
        aria-label={t('viewQuote.zoomOut')}
      >
        −
      </Button>
      <span className="text-sm font-medium text-gray-700 w-12 text-center">
        {Math.round(zoom * 100)}%
      </span>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="h-9 w-9 text-lg"
        disabled={zoom >= max}
        onClick={() => setZoom((z) => Math.min(max, +(z + 0.1).toFixed(2)))}
        aria-label={t('viewQuote.zoomIn')}
      >
        +
      </Button>
    </div>
  );

  const SummarySidebar = () => {
    const sidebarCopy = {
      quote: {
        title: t('viewQuote.quoteSidebarTitle'),
        hint: t('viewQuote.quoteSidebarHint'),
      },
      terms: {
        title: t('viewQuote.termsSidebarTitle'),
        hint: t('viewQuote.termsSidebarHint'),
      },
      sign: {
        title: t('viewQuote.signSidebarTitle'),
        hint: t('viewQuote.signatureInstruction'),
      },
    }[step];

    return (
      <aside className={`view-quote-summary-aside view-quote-desktop-aside ${step === 'quote' || step === 'terms' ? 'xl:col-span-3' : 'lg:col-span-1'}`}>
        <div className="xl:sticky xl:top-6 space-y-4">
          <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              {t('viewQuote.quoteReference')}
            </p>
            <p className="mt-1 text-lg font-semibold text-gray-900">{quote?.quote_number}</p>
            <div className="mt-4 border-t border-gray-100 pt-4">
              <p className="text-sm text-gray-500">{quote?.recipient_name}</p>
              <p className="mt-1 text-3xl font-bold text-gray-900">{formatCurrency(total)}</p>
              <p className="text-sm text-gray-500">{t('viewQuote.inclVat')}</p>
            </div>
            {sidebarCopy ? (
              <div className="mt-5 border-t border-gray-100 pt-4">
                <h2 className="text-base font-semibold text-gray-900">{sidebarCopy.title}</h2>
                <p className="mt-2 text-sm leading-relaxed text-gray-600">{sidebarCopy.hint}</p>
              </div>
            ) : null}
            {step === 'sign' ? (
              <Button
                className="mt-6 h-12 w-full bg-green-600 text-base hover:bg-green-700"
                disabled={!name.trim() || !email.trim() || !signature || submitting}
                onClick={handleSignSubmit}
              >
                {submitting ? t('viewQuote.submitting') : t('viewQuote.signAndAccept')}
              </Button>
            ) : null}
          </div>
          <div className="rounded-lg border border-orange-100 bg-orange-50 px-4 py-3 text-sm text-orange-900">
            <p className="font-medium">{t('viewQuote.secureProcessTitle')}</p>
            <p className="mt-1 text-orange-800/90">{t('viewQuote.secureProcessHint')}</p>
          </div>
        </div>
      </aside>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-orange-200 border-t-orange-600" />
        <p className="mt-4 text-base text-gray-600">{t('viewQuote.loading')}</p>
      </div>
    );
  }

  if (error === 'not_found' || (!quote && token)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="max-w-md rounded-xl border border-gray-200 bg-white p-8 text-center shadow-sm">
          <p className="text-lg font-medium text-gray-900">{t('viewQuote.quoteNotFound')}</p>
        </div>
      </div>
    );
  }

  const pdfExportHost = (
    <div
      ref={pdfExportRef}
      id="quote-content"
      className="view-quote-pdf-export-host quote-content-root bg-white"
    >
      {pdfExportDocument}
    </div>
  );

  if (step === 'success') {
    return (
      <>
        <div className="min-h-screen bg-slate-50">
          <header className="border-b border-gray-200 bg-white">
            <div className="mx-auto max-w-5xl px-4 py-5 sm:px-6">
              <p className="text-sm font-semibold uppercase tracking-wide text-orange-600">VMK Kozijnen B.V.</p>
              <h1 className="mt-1 text-2xl font-semibold text-gray-900">{t('viewQuote.pageTitle')}</h1>
            </div>
          </header>
          <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6">
            <ViewQuoteStepper currentStep="success" t={t} />
            <div className="mt-8 rounded-xl border border-gray-200 bg-white p-8 text-center shadow-sm sm:p-12">
              <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
                <Check className="h-10 w-10 text-green-600 stroke-[2.5]" />
              </div>
              <h2 className="text-2xl font-semibold text-gray-900">{t('viewQuote.acceptSuccessTitle')}</h2>
              <p className="mt-2 text-base text-gray-600">{t('viewQuote.acceptSuccess')}</p>
              <p className="mt-1 text-sm text-gray-500">{t('viewQuote.emailSentConfirmation')}</p>
              <div className="mx-auto mt-8 max-w-sm">
                <Button
                  onClick={handleDownloadSignedPdf}
                  disabled={downloading}
                  className="h-12 w-full bg-orange-600 text-base hover:bg-orange-700"
                >
                  <Download className="mr-2 h-4 w-4" />
                  {downloading ? t('viewQuote.downloading') : t('viewQuote.downloadSignedQuote')}
                </Button>
              </div>
            </div>
          </div>
        </div>
        {pdfExportHost}
      </>
    );
  }

  return (
    <>
      <div className="view-quote-page min-h-screen bg-slate-50 md:bg-slate-50">
        <h1 className="sr-only">
          {t('viewQuote.pageTitle')} — {quote?.quote_number}
        </h1>
        <header className="view-quote-desktop-header border-b border-gray-200 bg-white shadow-sm">
          <div className="mx-auto max-w-[90rem] px-4 py-5 sm:px-6 lg:px-8">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="text-sm font-semibold uppercase tracking-wide text-orange-600">
                  VMK Kozijnen B.V.
                </p>
                <h1 className="mt-1 text-2xl font-semibold text-gray-900">{t('viewQuote.pageTitle')}</h1>
                <p className="mt-1 text-sm text-gray-500">
                  {t('viewQuote.quoteReference')}: {quote?.quote_number}
                </p>
              </div>
              <div className="view-quote-page-header-price text-right">
                <p className="text-sm text-gray-500">{quote?.recipient_name}</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(total)}</p>
              </div>
            </div>
            <div className="mt-6 border-t border-gray-100 pt-6">
              <ViewQuoteStepper currentStep={step} t={t} />
            </div>
          </div>
        </header>

        <main className="view-quote-main mx-auto max-w-[90rem] px-4 py-8 sm:px-6 lg:px-8">
          <div
            className={
              step === 'quote' || step === 'terms'
                ? 'grid grid-cols-1 gap-8 xl:grid-cols-12'
                : 'grid grid-cols-1 gap-8 lg:grid-cols-3'
            }
          >
            <div
              className={
                step === 'quote' || step === 'terms' ? 'xl:col-span-9' : 'lg:col-span-2'
              }
            >
              {step === 'quote' ? (
                <section className="view-quote-quote-panel overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                  <div className="view-quote-desktop-toolbar flex flex-wrap items-center justify-between gap-4 border-b border-gray-100 bg-slate-50 px-6 py-4">
                    <div className="flex items-center gap-3">
                      <ClipboardList className="h-6 w-6 shrink-0 text-orange-600" />
                      <div>
                        <h2 className="text-xl font-semibold text-gray-900">
                          {t('viewQuote.quoteReviewTitle')}
                        </h2>
                        <p className="text-sm text-gray-600">{t('viewQuote.quoteReviewSubtitle')}</p>
                      </div>
                    </div>
                    <ZoomControls zoom={previewZoom} setZoom={setPreviewZoom} />
                  </div>

                  <div
                    ref={quoteScrollRef}
                    className="view-quote-pdf-scroll h-[min(72vh,900px)] sm:h-[72vh] overflow-y-auto overflow-x-hidden bg-slate-200/80 px-1 py-3 sm:px-4 sm:py-6"
                  >
                    <QuotePreviewScaler
                      key={quote?.quote_number || token}
                      zoom={previewZoom}
                      className="view-quote-pdf-preview"
                    >
                      {quotePreviewDocument}
                    </QuotePreviewScaler>
                    <div ref={quoteEndRef} className="h-10 w-full" aria-hidden />
                  </div>

                  <div className="view-quote-action-footer border-t border-gray-100 bg-white px-6 py-5 md:px-6 md:py-5">
                    {!quoteScrolledToEnd ? (
                      <p className="view-quote-scroll-hint mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                        {t('viewQuote.scrollQuoteToContinue')}
                      </p>
                    ) : (
                      <p className="view-quote-scroll-hint mb-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-900">
                        {t('viewQuote.quoteReviewComplete')}
                      </p>
                    )}
                    <div className="view-quote-open-pdf-btn mb-4">
                      <OpenPdfButton
                        label={t('viewQuote.openQuotePdf')}
                        loadingLabel={t('viewQuote.openingQuotePdf')}
                        loading={openingQuotePdf}
                        onClick={handleOpenQuotePdf}
                      />
                    </div>
                    <Button
                      className="h-12 w-full bg-orange-600 text-base hover:bg-orange-700"
                      disabled={!quoteScrolledToEnd}
                      onClick={handleContinueFromQuote}
                    >
                      {t('viewQuote.continueToTerms')}
                    </Button>
                  </div>
                </section>
              ) : null}

              {step === 'terms' ? (
                <section className="view-quote-terms-panel rounded-xl border border-gray-200 bg-white shadow-sm">
                  <div className="view-quote-desktop-toolbar flex flex-wrap items-center justify-between gap-4 border-b border-gray-100 bg-slate-50 px-6 py-4">
                    <div className="flex items-center gap-3">
                      <FileText className="h-6 w-6 shrink-0 text-orange-600" />
                      <div>
                        <h2 className="text-xl font-semibold text-gray-900">{t('viewQuote.termsTitle')}</h2>
                        <p className="text-sm text-gray-600">{t('viewQuote.termsSubtitle')}</p>
                      </div>
                    </div>
                    <ZoomControls zoom={previewZoom} setZoom={setPreviewZoom} />
                  </div>

                  <div
                    ref={termsScrollRef}
                    className="view-quote-terms-scroll h-[min(72vh,900px)] sm:h-[72vh] overflow-auto bg-slate-200/80 p-3 sm:p-6"
                  >
                    <p className="view-quote-desktop-toolbar mb-3 text-sm text-gray-600">
                      {t('viewQuote.termsMobileHint')}
                    </p>
                    <ViewQuoteTermsPdf
                      url={termsPdfUrl}
                      title={t('viewQuote.termsTitle')}
                      zoom={previewZoom}
                      onPageCount={setTermsPageCount}
                    />
                    <div ref={termsEndRef} className="h-10 w-full" aria-hidden />
                  </div>

                  <div className="view-quote-action-footer border-t border-gray-100 bg-white px-6 py-5">
                    <div className="view-quote-desktop-toolbar mb-4 flex flex-wrap gap-2">
                      <Button type="button" variant="outline" onClick={() => setStep('quote')}>
                        {t('viewQuote.backToQuote')}
                      </Button>
                    </div>
                    {!termsScrolledToEnd ? (
                      <p className="view-quote-scroll-hint mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                        {t('viewQuote.scrollToAccept')}
                      </p>
                    ) : (
                      <p className="view-quote-scroll-hint mb-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-900">
                        {t('viewQuote.termsReadComplete')}
                      </p>
                    )}
                    <div className="view-quote-open-pdf-btn mb-4">
                      <OpenPdfButton href={termsPdfUrl} label={t('viewQuote.openTermsPdf')} />
                    </div>
                    <Button
                      className="h-12 w-full bg-orange-600 text-base hover:bg-orange-700"
                      disabled={!termsScrolledToEnd}
                      onClick={handleAcceptTerms}
                    >
                      {t('viewQuote.acceptTerms')}
                    </Button>
                  </div>
                </section>
              ) : null}

              {step === 'sign' ? (
                <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm sm:p-10">
                  <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900">
                        {t('quote.template.agreementTitle')}
                      </h2>
                      <p className="mt-1 text-sm text-gray-600">{t('quote.template.agreementIntro')}</p>
                    </div>
                    <Button type="button" variant="outline" onClick={() => setStep('terms')}>
                      {t('viewQuote.backToTerms')}
                    </Button>
                  </div>

                  <div className="max-w-2xl space-y-6">
                    <div>
                      <Label className="text-sm font-medium text-gray-700">
                        {t('quote.template.agreementName')}
                      </Label>
                      <Input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="mt-2 h-11 text-base"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-700">
                        {t('viewQuote.emailAddress')}
                      </Label>
                      <Input
                        type="email"
                        value={email}
                        readOnly
                        className="mt-2 h-11 bg-gray-50 text-base"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-700">
                        {t('quote.template.agreementDate')}
                      </Label>
                      <Input value={signedDate} readOnly className="mt-2 h-11 bg-gray-50 text-base" />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-gray-700">
                        {t('quote.template.agreementSignature')}
                      </Label>
                      <canvas
                        ref={signatureCanvasRef}
                        width={560}
                        height={160}
                        className="mt-2 w-full cursor-crosshair touch-none rounded border-2 border-gray-300 bg-white"
                        style={{ maxWidth: '100%', height: '160px' }}
                        onMouseDown={startDraw}
                        onMouseMove={draw}
                        onMouseUp={endDraw}
                        onMouseLeave={endDraw}
                        onTouchStart={startDraw}
                        onTouchMove={draw}
                        onTouchEnd={endDraw}
                      />
                      <button
                        type="button"
                        onClick={clearSignature}
                        className="mt-2 text-sm text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        {t('viewQuote.clearField')}
                      </button>
                    </div>
                  </div>
                </section>
              ) : null}
            </div>

            <SummarySidebar />
          </div>
        </main>
      </div>
      {pdfExportHost}
    </>
  );
};

export default ViewQuotePage;
